// Frontend/src/main.tsx
import React from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import { RouterRoot } from "./router";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { Toaster } from "sonner";
import { PWAUpdateManager } from "./components/PWAUpdateBanner";
import { registerServiceWorker } from "./utils/sw-registration";
import { logCollector } from "./utils/log-collector";
import { queryClient } from "./lib/queryClient";
import { purgeLegacyCaches } from "./lib/clearCaches";

// Nothing patches Node.prototype here any more.
//
// This file used to install initPortalCleanup(), which wrapped
// Node.prototype.removeChild and swallowed NotFoundError. That error is
// React telling you the DOM it owns was mutated behind its back; swallowing
// it does not undo the divergence, it only lets React keep committing
// against a tree it no longer describes. The next reorder in that subtree
// then called insertBefore with a reference node that had moved, which
// throws the *uncatchable* "insertBefore ... not a child of this node"
// during the commit phase and takes the whole page down.
//
// Registering a payment was one way to hit it: the row's status text
// changes, the list re-sorts, a toast mounts and a modal unmounts, all in
// the same tick. See index.html for the translate opt-out that removes the
// usual source of the divergence.

// Initialize log collector
// The log collector auto-enables in dev mode (import.meta.env.DEV)
// In production builds, you can enable it manually via: localStorage.setItem('dev_logs_enabled', 'true')
// Then call: logCollector.enable()

// Log app version for debugging
console.log(
  `%c🚀 CondoGestión v${__APP_VERSION__}`,
  'font-size: 14px; font-weight: bold; color: #1F3A93;'
);
console.log(`📅 Build: ${__BUILD_TIME__}`);

// Drop the API response cache written by every build before this one.
//
// `/api/` is NetworkOnly now, so the stale invoice list can no longer be
// served -- but the bytes an already-installed PWA is carrying stay on disk
// until something deletes them, and they are one user's invoices on a device
// the next user logs into. The people carrying that data are exactly the
// installed base this is meant to protect.
void purgeLegacyCaches();

// Register service worker with update detection
registerServiceWorker((info) => {
  if (info.needsUpdate) {
    console.log('🔄 App update available!');
    // The PWAUpdateManager component will show the banner
  }
});

const container = document.getElementById("root");
if (!container) throw new Error("Root container #root not found");
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <RouterRoot />
          <PWAUpdateManager />
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
    <Toaster
      position="top-right"
      duration={2000}
      closeButton
      swipeDirections={["left", "right", "top", "bottom"]}
      richColors
      toastOptions={{
        classNames: {
          success:
            "bg-emerald-600 text-white border-emerald-700/60",
          error:
            "bg-rose-600 text-white border-rose-700/60",
          info:
            "bg-blue-600 text-white border-blue-700/60",
          warning:
            "bg-amber-500 text-white border-amber-600/60",
        },
      }}
    />
  </React.StrictMode>
);
